<script setup lang="ts">
import { computed } from 'vue';

import {
  buildRedlineSegments,
  parseSuggestionRedlines,
  type RedlineSegment,
  type ReplacementSuggestion,
} from '@/services/playbooks/playbookRunDisplay';

const props = defineProps<{
  suggestion: string | null | undefined;
}>();

const redlines = computed<ReplacementSuggestion[]>(() => parseSuggestionRedlines(props.suggestion));

/** Raw text shown when the suggestion isn't parseable redline JSON (legacy/prose). */
const fallbackText = computed(() => props.suggestion?.trim() ?? '');

function segments(redline: ReplacementSuggestion): RedlineSegment[] {
  return buildRedlineSegments(redline.string_to_replace, redline.replacement_string);
}
</script>

<template>
  <n-flex v-if="redlines.length" vertical :size="12">
    <n-flex v-for="(redline, index) in redlines" :key="index" vertical :size="4">
      <div class="suggestion-redline__diff">
        <span
          v-for="(segment, segmentIndex) in segments(redline)"
          :key="segmentIndex"
          :class="`suggestion-redline__segment suggestion-redline__segment--${segment.kind}`"
          >{{ segment.text }}</span
        >
      </div>
      <n-text v-if="redline.reason" :depth="3" style="font-size: 12px; line-height: 1.5">
        <strong>Reason:</strong> {{ redline.reason }}
      </n-text>
      <div v-if="redline.page">
        <n-tag size="small" :bordered="false">Page {{ redline.page }}</n-tag>
      </div>
    </n-flex>
  </n-flex>
  <n-text
    v-else-if="fallbackText"
    tag="p"
    :depth="3"
    style="
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
    "
    >{{ fallbackText }}</n-text
  >
</template>

<style scoped>
.suggestion-redline__diff {
  max-width: 100%;
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.suggestion-redline__segment--removed {
  color: #a33b36;
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
}

.suggestion-redline__segment--added {
  color: #166534;
  background: #dcfce7;
  border-radius: 3px;
  padding: 0 2px;
}
</style>
